/**
 * Response shapes the client validates against, where they differ from the
 * generated ones — and only where the wire contract carries more truth than
 * JSON-Schema could express.
 *
 * **One adjustment now, in one direction:** the batch envelope is *loosened* so
 * rows go unparsed. It is derived (`QuerySuccessSchema.extend`), so an unrelated
 * contract change to a tile lands here automatically.
 *
 * A discovery section used to be *tightened* here in the other direction, because
 * the backend modelled it as two `Optional` fields and "exactly one of
 * `fields`/`error`" could not survive the JSON-Schema round trip. It is a real
 * pydantic discriminated union now, so the generated
 * `FieldsSectionSchema` narrows on `success` on its own and the hand-written
 * re-statement is gone. Nothing hand-mirrors a wire shape in this file any more —
 * if you find yourself re-stating one, fix the model instead.
 *
 * ## The batch envelope: spine parsed, rows not
 *
 * `POST /bql` returns up to 15 tiles of up to 1000 rows. The generated
 * `BqlResponseSchema` types `rows` as `Record<string, unknown>[]`, so parsing it
 * whole walks every row of every tile on the hottest path in the product — and
 * proves nothing, because the row schema is an open record that accepts any
 * object anyway. The cost is real; the guarantee is not.
 *
 * So the parse is O(tiles), not O(rows): everything a caller *branches on* —
 * correlation id, the `success` discriminant, totals, errors, warnings, meta — is
 * validated, and `rows` passes through as `unknown[]` for the behavioural layer to
 * interpret when (and only when) it reads them.
 */
import { z } from 'zod'

import { QueryFailureSchema, QuerySuccessSchema } from './generated/schema'

/** A successful tile with its rows deliberately left unparsed. */
const QuerySuccessSpineSchema = QuerySuccessSchema.extend({
	rows: z.array(z.unknown()),
})

/**
 * One tile, spine-validated — exported so `batch-contract.ts` can parse the
 * batch response ONE TILE AT A TIME: a single tile carrying a
 * value this client's generated enums do not yet recognise (a new
 * `QueryWarning.code`, a new `QueryError.code`) must cost that tile, not the
 * up-to-`MAX_BATCH_QUERIES` siblings coalesced into the same request — the
 * exact promise `api.ts`'s `runBqlBatch` docblock states in writing.
 *
 * The discriminated union is preserved exactly as generated — narrowing on
 * `success` is the whole reason this contract is worth parsing, and it is the one
 * construct a naive emit gets wrong (the discriminator's wire mapping keys are
 * Python-stringified `"True"`/`"False"`).
 */
export const BqlResultSpineSchema = z.discriminatedUnion('success', [
	QuerySuccessSpineSchema,
	QueryFailureSchema,
])

/**
 * The batch response, spine-validated, ATOMICALLY. Kept for `envelope.test.ts`
 * and any caller that genuinely wants the whole-or-nothing parse; the live
 * `POST /bql` read path (`api.ts` → `batch-contract.ts`) does NOT use this —
 * it parses `results` as `unknown[]` and re-gates each tile with
 * `BqlResultSpineSchema` above via `parseArrayRows`, so one bad tile costs one
 * tile instead of the whole batch.
 */
export const BqlResponseSpineSchema = z.object({
	results: z.array(BqlResultSpineSchema),
})

/** The batch envelope as this layer hands it onward — identical to the wire shape but for `rows`. */
export type BqlResponseSpine = z.infer<typeof BqlResponseSpineSchema>
